import type { ModificationHandler } from "./ModificationHandler";
import type { UpdatePipeline } from "./UpdatePipeline";
import type { dispatch } from "../utils/events";
import type { XmlInjection, ITextReplacement } from "../global";
import inject from "./XmlInjection";
import { replaceText } from "./TextReplacement";

type ResolveState = "STARTED" | "STOPPED";

/** Dependencies required by ResolveExecutor to coordinate document modifications. */
export interface ResolveExecutorDependencies {
  modificationHandler: ModificationHandler;
  pipeline: UpdatePipeline;
  dispatch: typeof dispatch;
}

/** Coordinates document modifications with proper state management and event dispatching. */
export class ResolveExecutor {
  private _state: ResolveState = "STOPPED";
  private _deps: ResolveExecutorDependencies;

  constructor(deps: ResolveExecutorDependencies) {
    this._deps = deps;
  }

  /** Returns true if the executor is started and ready to process modifications. */
  get isStarted(): boolean {
    return this._state === "STARTED";
  }

  /** Enables the executor to process document modifications. */
  start(): void {
    this._state = "STARTED";
  }

  /** Disables the executor from processing document modifications. */
  stop(): void {
    this._state = "STOPPED";
  }

  /** Replaces text in the document while coordinating with modification handlers and updating the pipeline. */
  async replaceText(detail: ITextReplacement): Promise<void> {
    await this._execute(async () => {
      await Word.run(async (context) => {
        await replaceText(context, detail);
      });
    });
  }

  /** Injects XML into the document while coordinating with modification handlers and updating the pipeline. */
  async injectXml(detail: XmlInjection): Promise<void> {
    await this._execute(async () => {
      await Word.run((context) => inject(context, detail));
    });
  }

  /** Executes an action with proper state management, event dispatching, and error handling. */
  private async _execute(action: () => Promise<void>): Promise<void> {
    let isPaused = false;

    try {
      if (this._state === "STOPPED") {
        throw new Error("Add-in features are not enabled or loaded.");
      }

      this._deps.modificationHandler.pause();
      isPaused = true;

      try {
        await action();
        await this._deps.pipeline.update();
        this._deps.pipeline.ignoreNextModification();
      } finally {
        if (isPaused) {
          await this._deps.modificationHandler.resume(0);
        }
      }

      this._deps.dispatch("resolve-error-success");
    } catch (e) {
      this._deps.dispatch("resolve-error-failed", e);
      throw e;
    } finally {
      this._deps.dispatch("resolve-error-completed");
    }
  }
}
